import { motion } from 'framer-motion';
import { ChevronDown, Play, Star } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';



// Styled Components
const HeroSectionStyled = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #000000 0%, #1a1a2e 50%, #16213e 100%);
  
  /* Responsive styles */
  @media (max-width: 768px) {
    min-height: 100vh;
    height: 100vh;
  }
  
  @media (max-width: 480px) {
    min-height: 100vh;
    height: 100vh;
    align-items: stretch;
  }
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
  }
  
  @media (max-width: 480px) {
    width: 100vw;
    height: 100vh;
  }
`;

const GlowEffect = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 2rem;
  filter: blur(3rem);
  transform: scale(1.5);
`;

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%);
`;

const PhonePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .phone-container {
    position: relative;
    width: 20rem;
    height: 20rem;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 1.5rem;
    backdrop-filter: blur(0.5rem);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    
    .phone-inner {
      width: 12rem;
      height: 12rem;
      background: rgba(59, 130, 246, 0.2);
      border-radius: 1rem;
      transform: rotate(12deg);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  max-width: 80rem;
  margin: 0 auto;
  padding: 8rem 1rem 0;
  text-align: center;
  color: white;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    padding: 6rem 1rem 0;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 4rem 0.75rem 0;
    max-width: 100%;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(59, 130, 246, 0.2);
  backdrop-filter: blur(0.5rem);
  border-radius: 9999px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  span {
    font-size: 0.875rem;
    font-weight: 600;
  }
`;

const Title = styled.h1`
  font-size: clamp(3.5rem, 8vw, 6rem);
  font-weight: 900;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #60a5fa 50%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
`;

const Subtitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 3rem);
  font-weight: 300;
  margin-bottom: 1rem;
  color: #d1d5db;
`;

const Description = styled.p`
  font-size: clamp(1.125rem, 2vw, 1.5rem);
  color: #9ca3af;
  margin-bottom: 3rem;
  max-width: 64rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 3rem;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.375rem;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const FeaturePill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(0.5rem);
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem 0.625rem;
    font-size: 0.75rem;
    gap: 0.375rem;
  }
  
  .icon {
    font-size: 1rem;
    
    @media (max-width: 480px) {
      font-size: 0.875rem;
    }
  }
  
  .text {
    font-size: 0.875rem;
    font-weight: 500;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 4rem;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    gap: 1rem;
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    padding: 0 0.5rem;
  }
  
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const PrimaryButton = styled.button`
  position: relative;
  background: #007AFF;
  color: white;
  font-weight: 600;
  padding: 1rem 3rem;
  border-radius: 9999px;
  transition: all 0.3s ease;
  transform: scale(1);
  box-shadow: 0 10px 25px rgba(0, 122, 255, 0.3);
  border: none;
  cursor: pointer;
  font-size: 1.125rem;
  overflow: hidden;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #0056CC, #7c3aed);
    transform: scaleX(0);
    transition: transform 0.5s ease;
    transform-origin: left;
  }
  
  span {
    position: relative;
    z-index: 1;
  }
  
  &:hover {
    transform: scale(1.05);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 2px solid white;
  color: white;
  font-weight: 600;
  padding: 1rem 3rem;
  border-radius: 9999px;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 1.125rem;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
  
  &:hover {
    background: white;
    color: #007AFF;
    transform: scale(1.05);
  }
`;

const VideoButton = styled.button`
  margin: 0 auto 4rem auto;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.25rem 1.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  color: white;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    display: none;
  }
  
  @media (max-width: 480px) {
    display: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    color: white;
  }

  &:active {
    transform: translateY(0);
  }
  
  .play-icon {
    width: 4.5rem;
    height: 4.5rem;
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1;
    
    /* Responsive styles for play icon */
    @media (max-width: 768px) {
      height: 3.75rem;
      box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
    }
    
    @media (max-width: 480px) {
      width: 3rem;
      height: 3rem;
      box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
    }
    
    svg {
      width: 22px;
      height: 22px;
      color: white;
      margin-left: 3px;
      
      /* Responsive SVG */
      @media (max-width: 768px) {
        width: 18px;
        height: 18px;
      }
      
      @media (max-width: 480px) {
        width: 16px;
        height: 16px;
      }
    }
  }
  
  .text-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    
    .title {
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
      
      /* Responsive styles */
      @media (max-width: 768px) {
        font-size: 1rem;
        opacity: 0.95;
      }
      
      @media (max-width: 480px) {
        font-size: 0.95rem;
        opacity: 0.9;
      }
    }
    
    .subtitle {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.8);
      font-weight: 400;
      letter-spacing: 0.2px;
      
      /* Responsive styles */
      @media (max-width: 768px) {
        font-size: 0.85rem;
        opacity: 0.85;
      }
      
      @media (max-width: 480px) {
        font-size: 0.8rem;
        opacity: 0.8;
      }
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: translateX(-100%);
    transition: 0.6s;
    pointer-events: none;
  }

  &:hover::before {
    transform: translateX(100%);
  }
`;

const VideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  z-index: 1;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
  }
  
  @media (max-width: 480px) {
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
  }
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatItem = styled.div`
  text-align: center;
  
  .value {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 700;
    margin-bottom: 0.5rem;
    
    &.blue { color: #60a5fa; }
    &.purple { color: #a78bfa; }
    &.green { color: #34d399; }
    &.yellow { color: #fbbf24; }
  }
  
  .label {
    font-size: 0.875rem;
    color: #9ca3af;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: white;
  }
  
  span {
    font-size: 0.75rem;
    font-weight: 500;
  }
`;

const VideoModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.$isopen ? 1 : 0};
  visibility: ${props => props.$isopen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  /* Responsive styles */
  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 1rem;
  }
  
  /* Background cliquable pour fermer */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }
  
  .modal-content {
    background: white;
    border-radius: 1rem;
    padding: 2rem;
    max-width: 800px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    z-index: 2; /* Au-dessus du background cliquable */
    
    /* Responsive styles for modal content */
    @media (max-width: 768px) {
      width: 95%;
      max-width: 95%;
      padding: 1.5rem;
      max-height: 85vh;
      border-radius: 0.75rem;
    }
    
    @media (max-width: 480px) {
      width: 98%;
      max-width: 98%;
      padding: 1rem;
      max-height: 90vh;
      border-radius: 0.5rem;
    }
    
    .close-btn {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background: none;
      border: none;
      font-size: 2rem;
      cursor: pointer;
      color: #6b7280;
      transition: color 0.3s ease;
      
      /* Responsive styles for close button */
      @media (max-width: 768px) {
        top: 0.75rem;
        right: 0.75rem;
        font-size: 1.5rem;
      }
      
      @media (max-width: 480px) {
        top: 0.5rem;
        right: 0.5rem;
        font-size: 1.25rem;
      }
      
      &:hover {
        color: #1f2937;
      }
    }
    
    .video-placeholder {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 0.5rem;
      height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.5rem;
      margin-bottom: 1.5rem;
      
      /* Responsive styles for video placeholder */
      @media (max-width: 768px) {
        height: 250px;
        font-size: 1.25rem;
        margin-bottom: 1rem;
        border-radius: 0.375rem;
      }
      
      @media (max-width: 480px) {
        height: 200px;
        font-size: 1rem;
        margin-bottom: 0.75rem;
        border-radius: 0.25rem;
      }
    }
    
    h2 {
      color: #1f2937;
      margin-bottom: 1rem;
      
      /* Responsive styles for h2 */
      @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-bottom: 0.75rem;
      }
      
      @media (max-width: 480px) {
        font-size: 1.25rem;
        margin-bottom: 0.5rem;
      }
    }
    
    p {
      color: #6b7280;
      line-height: 1.6;
      margin-bottom: 1rem;
      
      /* Responsive styles for paragraphs */
      @media (max-width: 768px) {
        font-size: 0.95rem;
        margin-bottom: 0.75rem;
      }
      
      @media (max-width: 480px) {
        font-size: 0.875rem;
        margin-bottom: 0.5rem;
      }
    }
    
    .features-list {
      list-style: none;
      padding: 0;
      
      li {
        padding: 0.5rem 0;
        border-bottom: 1px solid #f3f4f6;
        color: #4b5563;
        
        /* Responsive styles for list items */
        @media (max-width: 768px) {
          padding: 0.375rem 0;
          font-size: 0.9rem;
        }
        
        @media (max-width: 480px) {
          padding: 0.25rem 0;
          font-size: 0.8rem;
        }
        
        &:before {
          content: "✓";
          color: #10b981;
          font-weight: bold;
          margin-right: 0.5rem;
        }
      }
    }
  }
`;

const HeroSection = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const navigate = useNavigate();

  const handleOrderNow = () => {
    // Redirection vers la boutique
    console.log('Redirection vers la boutique...');
    navigate('/boutique');
  };

  const handleCompareModels = () => {
    // Redirection vers la page de comparaison
    console.log('Redirection vers la comparaison des modèles...');
    navigate('/comparison/');
  };

  const handleVideoPresentation = () => {
    // Ouvrir/fermer le modal de présentation
    setIsVideoPlaying(!isVideoPlaying);
    if (!isVideoPlaying) {
      console.log('Ouverture de la présentation de l\'iPhone 17 Pro...');
    } else {
      console.log('Fermeture de la présentation...');
    }
  };

  const handleModalBackdropClick = (e) => {
    // Fermer le modal si on clique sur l'arrière-plan (mais pas sur le contenu)
    if (e.target === e.currentTarget) {
      setIsVideoPlaying(false);
    }
  };

  const handleScrollDown = () => {
    // Faire défiler vers la section suivante
    const nextSection = document.getElementById('features');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const features = [
    { icon: '🚀', text: 'Titanium aérospatial' },
    { icon: '📸', text: 'Système caméra pro' },
    { icon: '⚡', text: 'Puce A18 Pro' },
    { icon: '📱', text: 'Écran Super Retina XDR' },
    { icon: '🔋', text: 'Autonomie exceptionnelle' },
    { icon: '🎯', text: 'Action Mode stabilisé' },
  ];

  return (
    <HeroSectionStyled>
      <Background>
        <GlowEffect />
        <GradientOverlay />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              background: 'rgba(96, 165, 250, 0.3)',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `pulse ${3 + Math.random() * 4}s infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}

        {/* Vidéo locale */}
        <VideoContainer>
          <video
            autoPlay
            muted
            loop
            playsInline
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: '0',
              left: '0',
              border: 'none',
              objectFit: 'cover'
            }}
            onLoad={() => console.log('Vidéo locale chargée')}
          >
            <source src="/Introducing iPhone 17 Pro  Apple.mp4" type="video/mp4" />
            Votre navigateur ne supporte pas la lecture vidéo.
          </video>
        </VideoContainer>
      </Background>

      <Content>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Title */}
          <motion.div variants={itemVariants}>
            <Title>iPhone 17 Pro</Title>
          </motion.div>

          {/* Subtitle */}
          <motion.div variants={itemVariants}>
            <Subtitle>L'innovation rencontre l'élégance</Subtitle>
          </motion.div>

          {/* Description */}
          <motion.div variants={itemVariants}>
            <Description>
              En titanium aérospatial. La photographie réinventée. Une révolution dans votre poche.
            </Description>
          </motion.div>

          {/* Features */}
          <motion.div variants={itemVariants}>
            <FeaturesContainer>
              {features.map((feature, index) => (
                <FeaturePill key={index}>
                  <span className="icon">{feature.icon}</span>
                  <span className="text">{feature.text}</span>
                </FeaturePill>
              ))}
            </FeaturesContainer>
          </motion.div>

          {/* Buttons */}
          <motion.div variants={itemVariants}>
            <ButtonsContainer>
              <PrimaryButton onClick={handleOrderNow}>
                <span>Commander dès maintenant</span>
              </PrimaryButton>
              <SecondaryButton onClick={handleCompareModels}>Comparer les modèles</SecondaryButton>
            </ButtonsContainer>
          </motion.div>

          {/* Video Button */}
          <motion.div variants={itemVariants}>
            <VideoButton onClick={handleVideoPresentation}>
              <div className="play-icon">
                <Play style={{ width: '32px', height: '32px', marginLeft: '4px' }} />
              </div>
              <div className="text-content">
                <span className="title">Voir la présentation</span>
                <span className="subtitle">Découvrez toutes les innovations</span>
              </div>
            </VideoButton>
          </motion.div>

          {/* Stats */}
          <motion.div variants={itemVariants}>
            <StatsContainer>
              <StatItem>
                <div className="value blue">4K</div>
                <div className="label">Vidéo Dolby Vision</div>
              </StatItem>
              <StatItem>
                <div className="value purple">5x</div>
                <div className="label">Zoom optique</div>
              </StatItem>
              <StatItem>
                <div className="value green">29h</div>
                <div className="label">Autonomie</div>
              </StatItem>
              <StatItem>
                <div className="value yellow">120Hz</div>
                <div className="label">ProMotion</div>
              </StatItem>
            </StatsContainer>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            variants={itemVariants}
            animate={{
              y: [0, 10, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <ScrollIndicator onClick={handleScrollDown}>
              <ChevronDown style={{ width: '24px', height: '24px' }} />
              <span>Découvrir</span>
            </ScrollIndicator>
          </motion.div>
        </motion.div>
      </Content>
      
      <VideoModal $isopen={isVideoPlaying}>
  <div style={{
    maxWidth: '900px',
    margin: '2rem auto',
    background: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    position: 'relative',
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
    display: 'flex',
    gap: '2rem',
    
    /* Responsive styles */
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: '1rem',
      margin: '1rem auto',
      padding: '1rem',
      maxWidth: '95%'
    },
    
    '@media (max-width: 480px)': {
      flexDirection: 'column',
      gap: '0.75rem',
      margin: '0.5rem auto',
      padding: '0.75rem',
      maxWidth: '98%'
    }
  }}>
    
    {/* Bouton fermer */}
    <button 
      onClick={handleVideoPresentation}
      style={{
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        background: '#f5f5f7',
        border: 'none',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        fontSize: '1.3rem',
        cursor: 'pointer',
        zIndex: 10
      }}
    >
      ×
    </button>
    
    {/* Vidéo à gauche */}
    <div style={{
      flex: '1.2',
      borderRadius: '8px',
      overflow: 'hidden',
      minWidth: '0',
      position: 'relative',
      
      /* Responsive styles */
      '@media (max-width: 768px)': {
        flex: '1',
        order: '1',
        minHeight: '250px'
      },
      
      '@media (max-width: 480px)': {
        flex: '1',
        order: '1',
        minHeight: '200px'
      }
    }}>
      <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            width: '100%',
            height: '100%',
            minHeight: '300px',
            border: 'none',
            objectFit: 'cover',
            
            /* Responsive styles */
            '@media (max-width: 768px)': {
              width: '100%',
              height: '250px',
              minHeight: '250px',
              objectFit: 'cover'
            },
            
            '@media (max-width: 480px)': {
              width: '100%',
              height: '200px',
              minHeight: '200px',
              objectFit: 'cover'
            }
          }}
        >
        <source src="/Introducing iPhone 17 Pro  Apple.mp4" type="video/mp4" />
        Votre navigateur ne supporte pas la lecture vidéo.
      </video>
    </div>
    
    {/* Infos à droite */}
    <div style={{
      flex: '1',
      padding: '0.5rem'
    }}>
      
      {/* Titre */}
      <h2 style={{
        fontSize: '1.6rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        color: '#1d1d1f'
      }}>
        iPhone 17
      </h2>
      
      {/* Sous-titre */}
      <p style={{
        fontSize: '1rem',
        color: '#86868b',
        marginBottom: '1rem'
      }}>
        L'innovation à son apogée
      </p>
      
      {/* Description */}
      <p style={{
        color: '#424245',
        lineHeight: '1.6',
        marginBottom: '2rem',
        fontSize: '0.95rem'
      }}>
        Découvrez le futur avec l'iPhone 17. Conçu pour repousser les limites avec des matériaux premium et des performances exceptionnelles.
      </p>
      
      {/* Caractéristiques en liste */}
      <div style={{
        marginBottom: '2rem'
      }}>
        {[
          'Écran Super Retina XDR 6,7"',
          'Camera Pro 48MP avec zoom 5x',
          'Châssis en titane aérospatial',
          'Puce A18 Bionic',
          'Autonomie jusqu\'à 29h',
          'Résistance IP68'
        ].map((item, i) => (
          <div 
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '0.8rem',
              fontSize: '0.9rem'
            }}
          >
            <span style={{
              color: '#0071e3',
              marginRight: '0.8rem',
              fontSize: '1.1rem'
            }}>✓</span>
            <span>{item}</span>
          </div>
        ))}
      </div>
      
      {/* Prix */}
      <div style={{
        background: '#f5f5f7',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          fontSize: '0.9rem',
          color: '#86868b',
          marginBottom: '0.3rem'
        }}>
          À partir de
        </div>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: '#1d1d1f'
        }}>
          1 850 329 FCFA
        </div>
      </div>
      
      {/* Boutons */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.8rem'
      }}>
        <button 
          style={{
            background: '#0071e3',
            color: 'white',
            border: 'none',
            padding: '0.9rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem',
            width: '100%'
          }}
          onClick={() => window.open('https://www.apple.com/fr/', '_blank')}
        >
          Acheter maintenant
        </button>
        <button 
          style={{
            background: 'transparent',
            color: '#0071e3',
            border: '1px solid #d2d2d7',
            padding: '0.9rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '1rem',
            width: '100%'
          }}
          onClick={() => window.open('https://www.apple.com/fr/iphone/', '_blank')}
        >
          Plus d'informations
        </button>
      </div>
    </div>
  </div>
</VideoModal>
    </HeroSectionStyled>
  );
};

export default HeroSection;
